import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Applications = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Mock data - replace with actual API calls
  const applications = [
    {
      id: 'APP-001',
      jobId: 1,
      jobTitle: 'Senior Frontend Developer',
      company: 'TechCorp Inc.',
      companyLogo: '/api/placeholder/40/40',
      location: 'New York, NY',
      appliedDate: '2024-01-15',
      status: 'under_review',
      lastUpdate: '2024-01-16',
      matchScore: 95,
      salary: '$120,000 - $150,000',
      nextStep: 'Technical assessment',
      timeline: [
        { date: '2024-01-15', action: 'Application submitted', status: 'completed' },
        { date: '2024-01-16', action: 'Application viewed', status: 'completed' },
        { date: '2024-01-18', action: 'Technical assessment', status: 'upcoming' },
        { date: '', action: 'Interview', status: 'pending' },
        { date: '', action: 'Final decision', status: 'pending' }
      ]
    },
    {
      id: 'APP-002',
      jobId: 2,
      jobTitle: 'Full Stack Engineer',
      company: 'StartUp Labs',
      companyLogo: '/api/placeholder/40/40',
      location: 'San Francisco, CA',
      appliedDate: '2024-01-12',
      status: 'interview',
      lastUpdate: '2024-01-17',
      matchScore: 88,
      salary: '$100,000 - $130,000',
      nextStep: 'Technical interview scheduled for Jan 20',
      timeline: [
        { date: '2024-01-12', action: 'Application submitted', status: 'completed' },
        { date: '2024-01-13', action: 'Application viewed', status: 'completed' },
        { date: '2024-01-15', action: 'Phone screen completed', status: 'completed' },
        { date: '2024-01-20', action: 'Technical interview', status: 'upcoming' },
        { date: '', action: 'Final round', status: 'pending' }
      ]
    },
    {
      id: 'APP-003',
      jobId: 3,
      jobTitle: 'React Developer',
      company: 'Digital Solutions',
      companyLogo: '/api/placeholder/40/40',
      location: 'Austin, TX',
      appliedDate: '2024-01-10',
      status: 'rejected',
      lastUpdate: '2024-01-14',
      matchScore: 76,
      salary: '$90,000 - $120,000',
      nextStep: 'No further action required',
      timeline: [
        { date: '2024-01-10', action: 'Application submitted', status: 'completed' },
        { date: '2024-01-11', action: 'Application viewed', status: 'completed' },
        { date: '2024-01-14', action: 'Position filled', status: 'completed' }
      ],
      feedback: 'The position has been filled by another candidate. We encourage you to apply for future openings.'
    },
    {
      id: 'APP-004',
      jobId: 4,
      jobTitle: 'JavaScript Engineer',
      company: 'CodeCraft Inc.',
      companyLogo: '/api/placeholder/40/40',
      location: 'Remote',
      appliedDate: '2024-01-08',
      status: 'offer',
      lastUpdate: '2024-01-18',
      matchScore: 92,
      salary: '$110,000 - $140,000',
      nextStep: 'Review offer details',
      timeline: [
        { date: '2024-01-08', action: 'Application submitted', status: 'completed' },
        { date: '2024-01-09', action: 'Application viewed', status: 'completed' },
        { date: '2024-01-12', action: 'Technical interview', status: 'completed' },
        { date: '2024-01-15', action: 'Cultural fit interview', status: 'completed' },
        { date: '2024-01-18', action: 'Offer extended', status: 'completed' }
      ],
      offerDetails: {
        salary: '$135,000',
        bonus: '$10,000 signing bonus',
        startDate: '2024-02-15',
        benefits: 'Full health insurance, 401(k) matching, unlimited PTO'
      }
    },
    {
      id: 'APP-005',
      jobId: 5,
      jobTitle: 'UI/UX Developer',
      company: 'DesignHub',
      companyLogo: '/api/placeholder/40/40',
      location: 'Chicago, IL',
      appliedDate: '2024-01-05',
      status: 'withdrawn',
      lastUpdate: '2024-01-12',
      matchScore: 82,
      salary: '$85,000 - $110,000',
      nextStep: 'Application withdrawn',
      timeline: [
        { date: '2024-01-05', action: 'Application submitted', status: 'completed' },
        { date: '2024-01-06', action: 'Application viewed', status: 'completed' },
        { date: '2024-01-12', action: 'Application withdrawn', status: 'completed' }
      ]
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Applications', count: applications.length },
    { value: 'under_review', label: 'Under Review', count: applications.filter(app => app.status === 'under_review').length },
    { value: 'interview', label: 'Interview', count: applications.filter(app => app.status === 'interview').length },
    { value: 'offer', label: 'Offer', count: applications.filter(app => app.status === 'offer').length },
    { value: 'rejected', label: 'Rejected', count: applications.filter(app => app.status === 'rejected').length },
    { value: 'withdrawn', label: 'Withdrawn', count: applications.filter(app => app.status === 'withdrawn').length }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      under_review: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Review', icon: 'fas fa-clock' },
      interview: { color: 'bg-blue-100 text-blue-800', label: 'Interview', icon: 'fas fa-calendar' },
      offer: { color: 'bg-green-100 text-green-800', label: 'Offer Received', icon: 'fas fa-trophy' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Not Selected', icon: 'fas fa-times' },
      withdrawn: { color: 'bg-gray-100 text-gray-800', label: 'Withdrawn', icon: 'fas fa-ban' }
    };
    const config = statusConfig[status] || statusConfig.under_review;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <i className={`${config.icon} mr-2`}></i>
        {config.label}
      </span>
    );
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysAgo = (dateString) => {
    const today = new Date();
    const appliedDate = new Date(dateString);
    const diffTime = Math.abs(today - appliedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  };

  const filteredApplications = applications.filter(app => 
    statusFilter === 'all' || app.status === statusFilter
  );

  const stats = {
    total: applications.length,
    active: applications.filter(app => ['under_review', 'interview'].includes(app.status)).length,
    offers: applications.filter(app => app.status === 'offer').length,
    successRate: Math.round((applications.filter(app => app.status === 'offer').length / applications.length) * 100) || 0
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">My Applications</h1>
        <p className="text-gray-400">Track your job applications and interview progress</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Total Applications</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.total}</h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <i className="fas fa-file-alt text-blue-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Active Applications</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.active}</h3>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <i className="fas fa-play-circle text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Offers Received</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.offers}</h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <i className="fas fa-trophy text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Success Rate</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.successRate}%</h3>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg">
              <i className="fas fa-chart-line text-orange-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm">
        {/* Filters and Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2 mb-4 lg:mb-0">
              {statusOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setStatusFilter(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    statusFilter === option.value
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {option.label} ({option.count})
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Sort by:</span>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="match">Best Match</option>
                  <option value="status">Status</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="divide-y divide-gray-200">
          {filteredApplications.map((application) => (
            <div key={application.id} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                {/* Application Details */}
                <div className="flex-1">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-sm">
                      {application.company.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <Link 
                            to={`/candidate/jobs/${application.jobId}`}
                            className="text-lg font-semibold text-gray-800 hover:text-primary-600"
                          >
                            {application.jobTitle}
                          </Link>
                          <p className="text-gray-400">{application.company} • {application.location}</p>
                        </div>
                        <div className="flex items-center space-x-3 mt-2 sm:mt-0">
                          {getStatusBadge(application.status)}
                          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMatchScoreColor(application.matchScore)}`}>
                            <i className="fas fa-robot mr-1"></i>
                            {application.matchScore}%
                          </div>
                        </div>
                      </div>

                      {/* Application Timeline */}
                                            {/* Application Progress - Simplified Version */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Application Status</span>
                          <span className="text-sm text-gray-500">Applied {getDaysAgo(application.appliedDate)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 mb-2">
                          {application.timeline.map((step, index) => (
                            <div
                              key={index}
                              className={`flex flex-col items-center flex-1 ${
                                step.status === 'completed' ? 'text-green-600' : 
                                step.status === 'upcoming' ? 'text-blue-600' : 
                                'text-gray-400'
                              }`}
                            >
                              <div className={`w-3 h-3 rounded-full mb-1 ${
                                step.status === 'completed' ? 'bg-green-500' : 
                                step.status === 'upcoming' ? 'bg-blue-500' : 
                                'bg-gray-300'
                              }`}></div>
                              <span className="text-xs text-center capitalize">{step.action.replace('_', ' ')}</span>
                            </div>
                          ))}
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${(application.timeline.filter(step => step.status === 'completed').length / application.timeline.length) * 100}%` 
                            }}
                          ></div>
                        </div>
                      </div>


                      {/* <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Application Progress</span>
                          <span className="text-sm text-gray-500">Applied {getDaysAgo(application.appliedDate)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          {application.timeline.slice(0, 4).map((step, index) => (
                            <div
                              key={index}
                              className={`flex-1 h-2 rounded-full ${
                                step.status === 'completed'
                                  ? 'bg-green-500'
                                  : step.status === 'upcoming'
                                  ? 'bg-blue-500'
                                  : 'bg-gray-200'
                              }`}
                              title={step.action}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Applied</span>
                          <span>Current</span>
                          <span>Next</span>
                          <span>Decision</span>
                        </div>
                      </div> */}

                      {/* Next Step */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center">
                          <i className="fas fa-info-circle text-blue-500 mr-2"></i>
                          <span className="text-sm text-blue-700 font-medium">Next: {application.nextStep}</span>
                        </div>
                      </div>

                      {/* Offer Details (if applicable) */}
                      {application.status === 'offer' && application.offerDetails && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg">
                          <div className="flex items-center mb-2">
                            <i className="fas fa-trophy text-green-500 mr-2"></i>
                            <span className="text-sm text-green-700 font-medium">Offer Details</span>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-green-800">
                            <div>Salary: {application.offerDetails.salary}</div>
                            <div>Bonus: {application.offerDetails.bonus}</div>
                            <div>Start Date: {formatDate(application.offerDetails.startDate)}</div>
                          </div>
                        </div>
                      )}

                      {/* Feedback (if rejected) */}
                      {application.status === 'rejected' && application.feedback && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center">
                            <i className="fas fa-comment-alt text-red-500 mr-2"></i>
                            <span className="text-sm text-red-700">{application.feedback}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-2 mt-4 lg:mt-0 lg:ml-4">
                  <Link
                    to={`/candidate/jobs/${application.jobId}`}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm flex items-center justify-center"
                  >
                    <i className="fas fa-eye mr-2"></i>
                    View Job
                  </Link>
                  <Link
                    to={`/candidate/employer/${application.company.toLowerCase().replace(/\s+/g, '-')}`}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm flex items-center justify-center"
                  >
                    <i className="fas fa-building mr-2"></i>
                    Company
                  </Link>
                  {application.status === 'under_review' && (
                    <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm flex items-center justify-center">
                      <i className="fas fa-ban mr-2"></i>
                      Withdraw
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Empty State */}
          {filteredApplications.length === 0 && (
            <div className="text-center py-12">
              <i className="fas fa-file-alt text-4xl text-gray-300 mb-4"></i>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No applications found</h3>
              <p className="text-gray-600 mb-6">
                {statusFilter === 'all' 
                  ? "You haven't submitted any job applications yet."
                  : `You don't have any applications with status "${statusOptions.find(s => s.value === statusFilter)?.label}".`
                }
              </p>
              <Link
                to="/candidate/jobs"
                className="btn-primary text-white font-semibold py-2 px-6 rounded-lg inline-flex items-center"
              >
                <i className="fas fa-search mr-2"></i>
                Find Jobs
              </Link>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredApplications.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">1</span> to <span className="font-medium">{filteredApplications.length}</span> of{' '}
                <span className="font-medium">{filteredApplications.length}</span> applications
              </div>
              <div className="flex space-x-2">
                <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Previous
                </button>
                <button className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* AI Insights Card */}
      <div className="mt-8 bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">AI Application Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="flex items-start">
              <i className="fas fa-lightbulb text-yellow-500 mt-1 mr-3"></i>
              <p className="text-sm text-gray-600">
                Your applications have a {stats.successRate}% success rate. Consider applying to more roles that match your skills.
              </p>
            </div>
            <div className="flex items-start">
              <i className="fas fa-chart-line text-green-500 mt-1 mr-3"></i>
              <p className="text-sm text-gray-600">
                Companies typically respond within 5-7 days. Follow up if you haven't heard back after 10 days.
              </p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-start">
              <i className="fas fa-robot text-purple-500 mt-1 mr-3"></i>
              <p className="text-sm text-gray-600">
                Based on your profile, you're well-suited for Frontend and Full Stack roles. Focus your applications there.
              </p>
            </div>
            <div className="flex items-start">
              <i className="fas fa-clock text-blue-500 mt-1 mr-3"></i>
              <p className="text-sm text-gray-600">
                Update your resume and portfolio to increase your match scores by an average of 15%.
              </p>
            </div>
          </div>
        </div>
        <Link 
          to="/candidate/ai-assistant"
          className="block mt-4 text-center py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow font-medium"
        >
          <i className="fas fa-robot mr-2"></i>
          Get Personalized Application Strategy
        </Link>
      </div>
    </div>
  );
};

export default Applications;